import { createContext, useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardGroup, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import ScoreSheetHeader from './scoresheetheader';
import Header from '../helpers/header';
import MenuBubble from '../helpers/menububble';
import UserBubble from '../helpers/userbubble';
import {
  SessionSubscriptionManager,
  useRotations,
  useExperimental,
  useEvalConfig,
  useJudgesByApparatus,
  useTeamsByAthleteId,
} from './hooks';
import { eye, eyeSlash, printIcon } from '../helpers/icons';
import './main.css';
import './scoreslips.css';
import { GYMapparatusAbbv, teamBrand } from '../../utilities/conversions';
import * as Icons from '../helpers/icons';
import { useQueryParams } from '../../utilities/query';

function ScoreSlipsHeader({
  showBlank,
  setShowBlank,
  columnFirst,
  setColumnFirst,
  slipsPerPage,
  setSlipsPerPage,
  queryParams,
  setQueryParams,
}) {
  const handleBlankClick = () => {
    setQueryParams({
      ...queryParams,
      scoreslips: !showBlank ? 'BLANK' : '',
    });
    setShowBlank(!showBlank);
  };

  const handleColumnFirstClick = () => {
    setColumnFirst(!columnFirst);
  };

  const handleSlipsPerPageClick = () => {
    setSlipsPerPage(slipsPerPage === 6 ? 4 : slipsPerPage === 4 ? 8 : 6);
  };

  return (
    <Header className="noprint">
      <MenuBubble />
      <div className="scoreSheetBlankButton">
        <Button variant="outline-secondary" onClick={handleBlankClick}>
          {showBlank ? eyeSlash : eye}
        </Button>
      </div>
      <div className="scoreSheetOrderButton">
        <Button
          variant={columnFirst ? 'secondary' : 'outline-secondary'}
          onClick={handleColumnFirstClick}
          title={
            columnFirst
              ? 'Column order (1-4-7, 2-5-8, 3-6-9)'
              : 'Sequential order (1-2-3...)'
          }
        >
          {columnFirst ? '1-4-7' : '1-2-3'}
        </Button>
      </div>
      <div className="scoreSheetSlipsButton">
        <Button
          variant={slipsPerPage !== 6 ? 'secondary' : 'outline-secondary'}
          onClick={handleSlipsPerPageClick}
          title={`${slipsPerPage} slips per page`}
        >
          {`${slipsPerPage}/pg`}
        </Button>
      </div>
      <div className="scoreSheetPrintButton">
        <Button variant="outline-secondary" onClick={() => window.print()}>
          {printIcon}
        </Button>
      </div>
      <UserBubble />
    </Header>
  );
}

export const SessionContext = createContext(null);

export default function ScoreSlips({ sessionId }) {
  const evalConfig = useEvalConfig();
  const judgesByApparatus = useJudgesByApparatus(sessionId);
  const { jPanel, ePanel } = evalConfig;
  const experimental = useExperimental({ sessionId });
  const rotations = useRotations(sessionId);
  const { session } = experimental;
  const { gender } = session || {};
  const apparatusesInOrder = GYMapparatusAbbv(gender);
  const teamA = useSelector((state) => state.producer['teamA']);
  const teamsByAthleteId = useTeamsByAthleteId(sessionId);
  const teamPenaltyLabel = evalConfig?.utl ? 'UTL' : 'ND (Team)';

  const queryParams = useQueryParams();
  const [showBlank, setShowBlank] = useState(
    queryParams.get('scoreslips')?.toUpperCase() === 'BLANK'
  );
  const [columnFirst, setColumnFirst] = useState(false);
  const [slipsPerPage, setSlipsPerPage] = useState(6);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  useEffect(() => {
    if (isSafari) {
      document.documentElement.classList.add('safari');
    }
    document.documentElement.classList.toggle(`slips-${slipsPerPage}`, true);
    return () => {
      document.documentElement.classList.remove('safari');
      document.documentElement.classList.remove(`slips-${slipsPerPage}`);
    };
  }, [isSafari, slipsPerPage]);
  const lineup = useSelector((state) =>
    state.session.lineups.items.find((l) => l.id === teamA?.lineupId)
  );

  if (!Object.keys(judgesByApparatus).length || !teamA) {
    const sessionKey = queryParams.get('s');
    window.location.replace(`/session?s=${sessionKey}`);
    return null;
  }

  const whichPanel = jPanel || ePanel || [];

  // Reorder lineup for column-first display (1-4-7, 2-5-8, 3-6-9)
  // Always uses a 3x3 grid (9 positions) with blanks for missing athletes
  const reorderColumnFirst = (lineup) => {
    if (!lineup || lineup.length === 0) return lineup;
    const numColumns = 3;
    const numRows = 3; // Always 3 rows for 9 positions
    const reordered = [];
    for (let col = 0; col < numColumns; col++) {
      for (let row = 0; row < numRows; row++) {
        const idx = row * numColumns + col;
        if (idx < lineup.length) {
          reordered.push({
            ...lineup[idx],
            originalIndex: idx,
            isBlank: false,
          });
        } else {
          // Add blank placeholder for missing athlete
          reordered.push({
            key: `blank-${idx}`,
            originalIndex: idx,
            isBlank: true,
            name: '',
            athleteId: null,
          });
        }
      }
    }
    return reordered;
  };
  const fullLineup = JSON.parse(lineup.lineupData) || [];

  const scoreSlipsContent = (
    <section>
      {Object.values(rotations).map((rotation) => {
        const stagesByApparatus = rotation.stages.items.reduce((acc, stage) => {
          if (!stage._deleted) {
            acc[stage.apparatus] = stage;
          }
          return acc;
        }, {});

        let apparatus = '';

        Object.keys(stagesByApparatus).forEach((key) => {
          if (
            stagesByApparatus[key].squad?.rosters?.items?.some(
              (r) =>
                // Fix to check for lineup matching first and then team only as a fallback if no lineup attached
                (!r?._deleted &&
                  r?.lineupId &&
                  r?.lineupId === teamA.lineupId) ||
                (!r?.lineupId && r?.roster?.teamId === teamA.id)
            )
          ) {
            apparatus = key;
          }
        });

        const apparatusIndex = apparatusesInOrder.indexOf(apparatus);
        const currentLineup = fullLineup?.[apparatusIndex + 1];
        const isLineupSet = currentLineup && currentLineup.length > 0;
        // Apply column-first reordering if enabled
        const displayLineup = columnFirst
          ? reorderColumnFirst(currentLineup)
          : currentLineup?.map((l, i) => ({ ...l, originalIndex: i }));

        return (
          <section key={`${rotation.order}`} className="scoreSlipSection">
            <h3 className="noprint">Rotation {rotation.order + 1}</h3>

            <Row className="headers noprint" style={{ marginBottom: '1rem' }}>
              <Col className="left">
                <ScoreSheetHeader
                  teamSide="teamA"
                  left={true}
                  index={apparatusIndex + 1}
                />
              </Col>
            </Row>

            {(() => {
              if (!isLineupSet) return null;
              const allSlips = displayLineup.flatMap((l) => {
                const athleteTeam = l.isBlank
                  ? null
                  : teamsByAthleteId[l.athleteId];
                const { name: teamName } = teamBrand(athleteTeam);
                const displayNumber = l.originalIndex + 1;
                return whichPanel.map((j) => ({
                  key: `${l.key}_${j.type}`,
                  l,
                  j,
                  athleteTeam,
                  teamName,
                  displayNumber,
                }));
              });
              const chunks = [];
              for (let i = 0; i < allSlips.length; i += slipsPerPage) {
                chunks.push(allSlips.slice(i, i + slipsPerPage));
              }
              return chunks.map((chunk, ci) => (
                <Row
                  key={ci}
                  className={ci > 0 ? 'scoreSlipPage' : undefined}
                  style={{ justifyContent: 'center' }}
                >
                  {chunk.map(({ key, l, j, teamName, displayNumber }) => (
                    <Card
                      style={{
                        margin: '1rem',
                        pageBreakAfter: 'auto',
                        pageBreakInside: 'avoid',
                        minWidth: '46%',
                      }}
                      key={key}
                      className="scoreSlip"
                    >
                      <Card.Header style={{ fontSize: '1rem' }}>
                        <Row>
                          <Col xs={8}>
                            <h4>{session?.name}</h4>
                          </Col>
                          <Col xs={4}>
                            <h4 style={{ textAlign: 'center' }}>
                              R{rotation.order + 1}: #{displayNumber} ({j.type})
                            </h4>
                          </Col>
                        </Row>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col xs={9}>
                            <h2>{showBlank ? '' : l.name}</h2>
                          </Col>
                          <Col xs={3}>
                            <h4 style={{ textAlign: 'center' }}>{apparatus}</h4>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={9}>
                            <h4>{teamName}</h4>
                          </Col>
                          <Col xs={3} className="vCenter">
                            <span
                              style={{
                                width: '100%',
                                textAlign: 'center',
                                padding: '0 2rem',
                              }}
                            >
                              {Icons?.[`${apparatus}noText`]}
                            </span>
                          </Col>
                        </Row>
                        <Row>
                          <CardGroup style={{ width: '100%', margin: '1rem' }}>
                            <Card style={{ flexBasis: '25%' }}>
                              <Card.Header
                                style={{
                                  fontSize: '1.2rem',
                                  fontWeight: '600',
                                  padding: '0.25rem',
                                }}
                              >
                                SV
                              </Card.Header>
                              <Card.Body style={{ height: '6rem' }}></Card.Body>
                            </Card>
                            <Card style={{ flexBasis: '25%' }}>
                              <Card.Header
                                style={{
                                  fontSize: '1.2rem',
                                  fontWeight: '600',
                                  padding: '0.25rem',
                                }}
                              >
                                ND
                              </Card.Header>
                              <Card.Body></Card.Body>
                            </Card>
                            <Card style={{ flexBasis: '50%' }}>
                              <Card.Header
                                style={{
                                  fontSize: '1.2rem',
                                  fontWeight: '600',
                                  padding: '0.25rem',
                                }}
                              >
                                Score ({j.type})
                              </Card.Header>
                              <Card.Body></Card.Body>
                            </Card>
                          </CardGroup>
                        </Row>
                        <Row>
                          <CardGroup style={{ width: '100%', margin: '1rem' }}>
                            <Card style={{ flexBasis: '25%' }}>
                              <Card.Header
                                style={{
                                  fontSize: '1.2rem',
                                  fontWeight: '600',
                                  padding: '0.25rem',
                                }}
                              >
                                Final Avg.
                              </Card.Header>
                              <Card.Body style={{ height: '6rem' }}></Card.Body>
                            </Card>
                            <Card style={{ flexBasis: '25%' }}>
                              <Card.Header
                                style={{
                                  fontSize: '1.2rem',
                                  fontWeight: '600',
                                  padding: '0.25rem',
                                }}
                              >
                                {teamPenaltyLabel}
                              </Card.Header>
                              <Card.Body></Card.Body>
                            </Card>
                            <Card style={{ flexBasis: '50%' }}>
                              <Card.Header
                                style={{
                                  fontSize: '1.2rem',
                                  fontWeight: '600',
                                  padding: '0.25rem',
                                }}
                              >
                                Signature
                              </Card.Header>
                              <Card.Body></Card.Body>
                              <Card.Footer className="judgeSignature">
                                {judgesByApparatus[apparatus].component[j.type]}
                              </Card.Footer>
                            </Card>
                          </CardGroup>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                </Row>
              ));
            })()}
          </section>
        );
      })}
    </section>
  );

  return (
    <SessionContext.Provider value={experimental}>
      <ScoreSlipsHeader
        showBlank={showBlank}
        setShowBlank={setShowBlank}
        columnFirst={columnFirst}
        setColumnFirst={setColumnFirst}
        slipsPerPage={slipsPerPage}
        setSlipsPerPage={setSlipsPerPage}
        queryParams={queryParams}
        setQueryParams={(newParams) =>
          queryParams.set('scoreslips', newParams.scoreslips)
        }
      />
      <div
        className={`main session slips-${slipsPerPage}${
          isSafari ? ' safari' : ''
        }`}
      >
        <Container>{scoreSlipsContent}</Container>
        <SessionSubscriptionManager sessionId={sessionId} />
      </div>
    </SessionContext.Provider>
  );
}
